import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from '@monksflow/monks-ui';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { ArtifactPreviewFallback } from './ArtifactPreviewFallback';

interface ResolveArtifactPreviewProps<T> {
  /** Query-key prefix; the resolved workspace id and artifact id are appended. */
  queryKey: string;
  artifactId: string;
  /** Fetches the snapshot. Only invoked once a workspace is resolved. */
  queryFn: (workspaceId: string, artifactId: string) => Promise<T>;
  /** Renders the resolved snapshot. */
  render: (data: T) => ReactNode;
  /** Saved snapshots are immutable, so this defaults to never-stale. */
  staleTime?: number;
}

/**
 * Shared resolver for Library artifact previews that read a snapshot by id.
 * Resolves the workspace, runs the query, and owns the spinner / fallback /
 * content states so each preview only declares its query and how to render it.
 */
export function ResolveArtifactPreview<T>({
  queryKey,
  artifactId,
  queryFn,
  render,
  staleTime = Infinity,
}: ResolveArtifactPreviewProps<T>) {
  const workspaceId = useCurrentWorkspaceId();

  const query = useQuery({
    queryKey: [queryKey, workspaceId, artifactId],
    enabled: !!workspaceId,
    queryFn: () => queryFn(workspaceId!, artifactId),
    staleTime,
  });

  if (!workspaceId || query.isError) {
    return <ArtifactPreviewFallback />;
  }

  if (query.isPending) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <Spinner />
      </div>
    );
  }

  return <>{render(query.data)}</>;
}
